import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  Clock3,
  Loader2,
  Map as MapIcon,
  Plus,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';

type Resource = {
  id: string;
  name: string;
  type: string;
  active: boolean;
  locationId?: string | null;
  description?: string | null;
};

type Reservation = {
  id: string;
  resourceId: string;
  title: string;
  start: string;
  end: string;
  status: string;
  resource?: {
    id: string;
    name: string;
    type: string;
  };
};

type AppointmentResourceAttachment = {
  id: string;
  role: 'room' | 'asset';
  resourceId: string;
  resourceName: string;
  resourceType?: string | null;
};

type AppointmentRecord = {
  id: string;
  title?: string | null;
  status: string;
  startTime: string;
  endTime: string;
  patient: { id: string; name: string };
  provider: { id: string; name: string };
  resources?: AppointmentResourceAttachment[];
};

type FloorPlanRoom = {
  id: string;
  resourceId: string;
  label?: string | null;
  shapeType: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string | null;
  notes?: string | null;
  resource?: Resource;
};

type FloorPlan = {
  id: string;
  name: string;
  floorCode: string;
  floorLabel: string;
  imageUrl: string;
  imageWidth?: number | null;
  imageHeight?: number | null;
  active: boolean;
  rooms: FloorPlanRoom[];
};

type RoomStatus = 'available' | 'occupied' | 'maintenance';

const REFRESH_INTERVAL_MS = 30_000;

function toLocalInput(date: Date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInput(value: string) {
  return new Date(value);
}

function startOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function endOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

function roundToQuarterHour(date: Date) {
  const next = new Date(date);
  next.setSeconds(0, 0);
  const minutes = next.getMinutes();
  const rounded = Math.ceil(minutes / 15) * 15;
  next.setMinutes(rounded);
  return next;
}

function combineDateAndTime(date: Date, value: string) {
  const [hoursText, minutesText] = value.split(':');
  const next = new Date(date);
  next.setHours(Number(hoursText || 0), Number(minutesText || 0), 0, 0);
  return next;
}

function toTimeInput(date: Date) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function isReservationActive(status: string) {
  return !['cancelled', 'released'].includes(status.toLowerCase());
}

function overlapsSnapshot(start: string, end: string, snapshot: Date) {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return startDate <= snapshot && endDate > snapshot;
}

function formatRange(start: string, end: string, locale: string) {
  return `${new Date(start).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} - ${new Date(end).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}`;
}

function statusTone(status: RoomStatus) {
  if (status === 'occupied') return 'border-rose-200 bg-rose-100/90 text-rose-900';
  if (status === 'maintenance') return 'border-slate-300 bg-slate-200/85 text-slate-700';
  return 'border-emerald-200 bg-emerald-100/90 text-emerald-900';
}

function statusPillTone(status: RoomStatus) {
  if (status === 'occupied') return 'bg-rose-100 text-rose-700';
  if (status === 'maintenance') return 'bg-slate-100 text-slate-600';
  return 'bg-emerald-100 text-emerald-700';
}

function reservationTone(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === 'appointment') return 'bg-sky-50 text-sky-700 border-sky-200';
  if (normalized === 'maintenance') return 'bg-slate-100 text-slate-700 border-slate-200';
  if (normalized === 'blocked') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (normalized === 'cancelled' || normalized === 'released') return 'bg-slate-100 text-slate-500 border-slate-200';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
}

function isAppointmentReservation(reservation: Reservation, appointmentByReservationId: Map<string, AppointmentRecord>) {
  return reservation.title.startsWith('appt:') || reservation.status.toLowerCase() === 'appointment' || appointmentByReservationId.has(reservation.id);
}

export default function RoomManagement() {
  const navigate = useNavigate();
  const { fetchApi, fetchJson } = useApiClient();
  const { tr, language } = useI18n();
  const locale = language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US';

  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));
  const [snapshotTime, setSnapshotTime] = useState(() => toTimeInput(new Date()));
  const [resources, setResources] = useState<Resource[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [appointments, setAppointments] = useState<AppointmentRecord[]>([]);
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [selectedFloorIds, setSelectedFloorIds] = useState<string[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingForm, setBookingForm] = useState({
    resourceId: '',
    title: '',
    start: '',
    end: '',
    status: 'booked',
  });

  const rangeStart = useMemo(() => startOfDay(selectedDate), [selectedDate]);
  const rangeEnd = useMemo(() => endOfDay(selectedDate), [selectedDate]);
  const rangeStartIso = rangeStart.toISOString();
  const rangeEndIso = rangeEnd.toISOString();
  const snapshotDate = useMemo(() => combineDateAndTime(selectedDate, snapshotTime), [selectedDate, snapshotTime]);

  const loadData = useCallback(
    async (softRefresh = false) => {
      if (softRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const [resourceData, reservationData, appointmentData, floorPlanData] = await Promise.all([
          fetchJson<Resource[]>('/resources?includeInactive=true'),
          fetchJson<Reservation[]>(
            `/resources/reservations?start=${encodeURIComponent(rangeStartIso)}&end=${encodeURIComponent(rangeEndIso)}&includeCancelled=true`,
          ),
          fetchJson<AppointmentRecord[]>(
            `/appointments?start=${encodeURIComponent(rangeStartIso)}&end=${encodeURIComponent(rangeEndIso)}`,
          ),
          fetchJson<FloorPlan[]>('/resources/floor-plans'),
        ]);
        setResources(resourceData);
        setReservations(reservationData);
        setAppointments(appointmentData);
        setFloorPlans(floorPlanData.filter((plan) => plan.active));
      } catch (err: any) {
        setError(err?.message || tr('Failed to load room management data.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchJson, rangeEndIso, rangeStartIso, tr],
  );

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadData(true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    if (!floorPlans.length) {
      setSelectedFloorIds([]);
      setSelectedRoomId(null);
      return;
    }
    setSelectedFloorIds((current) => {
      const next = current.filter((id) => floorPlans.some((plan) => plan.id === id));
      return next.length ? next : floorPlans.map((plan) => plan.id);
    });
  }, [floorPlans]);

  const appointmentByReservationId = useMemo(() => {
    const map = new Map<string, AppointmentRecord>();
    appointments.forEach((appointment) => {
      (appointment.resources || []).forEach((attachment) => {
        map.set(attachment.id, appointment);
      });
    });
    return map;
  }, [appointments]);

  const selectedFloorPlans = useMemo(
    () => floorPlans.filter((plan) => selectedFloorIds.includes(plan.id)),
    [floorPlans, selectedFloorIds],
  );

  const roomSnapshotRows = useMemo(() => {
    return selectedFloorPlans.map((plan) => ({
      ...plan,
      rooms: plan.rooms
        .map((room) => {
          const resource = room.resource || resources.find((item) => item.id === room.resourceId);
          const roomReservations = reservations
            .filter((reservation) => reservation.resourceId === room.resourceId)
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
          const currentReservation = roomReservations.find(
            (reservation) => isReservationActive(reservation.status) && overlapsSnapshot(reservation.start, reservation.end, snapshotDate),
          );
          const nextReservation = roomReservations.find(
            (reservation) => isReservationActive(reservation.status) && new Date(reservation.start).getTime() > snapshotDate.getTime(),
          );
          const status: RoomStatus = !resource?.active ? 'maintenance' : currentReservation ? 'occupied' : 'available';
          return {
            ...room,
            resource,
            status,
            currentReservation,
            nextReservation,
            currentAppointment: currentReservation ? appointmentByReservationId.get(currentReservation.id) : undefined,
            nextAppointment: nextReservation ? appointmentByReservationId.get(nextReservation.id) : undefined,
          };
        })
        .sort((a, b) => (a.label || a.resource?.name || '').localeCompare(b.label || b.resource?.name || '')),
    }));
  }, [appointmentByReservationId, reservations, resources, selectedFloorPlans, snapshotDate]);

  const visibleRooms = useMemo(() => roomSnapshotRows.flatMap((plan) => plan.rooms), [roomSnapshotRows]);

  useEffect(() => {
    if (!visibleRooms.length) {
      setSelectedRoomId(null);
      return;
    }
    setSelectedRoomId((current) => (current && visibleRooms.some((room) => room.id === current) ? current : visibleRooms[0].id));
  }, [visibleRooms]);

  const selectedRoom = useMemo(
    () => visibleRooms.find((room) => room.id === selectedRoomId) ?? null,
    [selectedRoomId, visibleRooms],
  );

  const visibleResourceIds = useMemo(() => new Set(visibleRooms.map((room) => room.resourceId)), [visibleRooms]);

  const visibleReservations = useMemo(
    () =>
      reservations
        .filter((reservation) => visibleResourceIds.has(reservation.resourceId))
        .slice()
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
    [reservations, visibleResourceIds],
  );

  const availableRoomCount = visibleRooms.filter((room) => room.status === 'available').length;
  const occupiedRoomCount = visibleRooms.filter((room) => room.status === 'occupied').length;
  const maintenanceRoomCount = visibleRooms.filter((room) => room.status === 'maintenance').length;

  const selectedRoomReservations = useMemo(
    () =>
      selectedRoom
        ? reservations
            .filter((reservation) => reservation.resourceId === selectedRoom.resourceId)
            .slice()
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
        : [],
    [reservations, selectedRoom],
  );

  const openBookingModal = (resourceId?: string) => {
    const rounded = roundToQuarterHour(snapshotDate);
    const end = new Date(rounded.getTime() + 30 * 60 * 1000);
    setBookingForm({
      resourceId: resourceId || '',
      title: '',
      start: toLocalInput(rounded),
      end: toLocalInput(end),
      status: 'booked',
    });
    setBookingError(null);
    setShowBookingModal(true);
  };

  const submitBooking = async () => {
    setBookingError(null);
    if (!bookingForm.resourceId || !bookingForm.title.trim() || !bookingForm.start || !bookingForm.end) {
      setBookingError(tr('Resource, title, start, and end are required.'));
      return;
    }
    const startDate = fromLocalInput(bookingForm.start);
    const endDate = fromLocalInput(bookingForm.end);
    if (endDate <= startDate) {
      setBookingError(tr('End time must be after start time.'));
      return;
    }

    setBookingBusy(true);
    try {
      const response = await fetchApi('/resources/reservations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resourceId: bookingForm.resourceId,
          title: bookingForm.title.trim(),
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          status: bookingForm.status,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || tr('Failed to create reservation.'));
      }
      setNotice(tr('Reservation created.'));
      setShowBookingModal(false);
      await loadData(true);
    } catch (err: any) {
      setBookingError(err?.message || tr('Failed to create reservation.'));
    } finally {
      setBookingBusy(false);
    }
  };

  const deleteReservation = async (reservation: Reservation) => {
    if (isAppointmentReservation(reservation, appointmentByReservationId)) return;
    const confirmed = window.confirm(tr('Release this reservation?'));
    if (!confirmed) return;
    try {
      const response = await fetchApi(`/resources/reservations/${reservation.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(tr('Failed to release reservation.'));
      setNotice(tr('Reservation released.'));
      await loadData(true);
    } catch (err: any) {
      setError(err?.message || tr('Failed to release reservation.'));
    }
  };

  const toggleFloor = (floorId: string) => {
    setSelectedFloorIds((current) => {
      if (current.includes(floorId)) {
        return current.length === 1 ? current : current.filter((id) => id !== floorId);
      }
      return [...current, floorId];
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-slate-400">{tr('Operations')}</p>
          <h1 className="text-2xl font-semibold text-ink">{tr('Visual Room Management')}</h1>
          <p className="max-w-3xl text-sm text-slate-500">
            {tr('Track room availability on the actual floor plans, switch across one or many floors, and keep manual room bookings tied to the same shared resources.')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-300"
            onClick={() => setSelectedDate((prev) => startOfDay(new Date(prev.getTime() - 24 * 60 * 60 * 1000)))}
          >
            {tr('Prev day')}
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-300"
            onClick={() => {
              const now = new Date();
              setSelectedDate(startOfDay(now));
              setSnapshotTime(toTimeInput(now));
            }}
          >
            <CalendarDays size={16} />
            {tr('Today')}
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-300"
            onClick={() => setSelectedDate((prev) => startOfDay(new Date(prev.getTime() + 24 * 60 * 60 * 1000)))}
          >
            {tr('Next day')}
          </button>
          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
            <Clock3 size={16} />
            <input
              type="time"
              value={snapshotTime}
              onChange={(event) => setSnapshotTime(event.target.value)}
              className="bg-transparent outline-none"
            />
          </label>
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-300"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {tr('Refresh')}
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-300"
            onClick={() => navigate('/settings/room-management')}
          >
            <Settings2 size={16} />
            {tr('Manage floors')}
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white"
            onClick={() => openBookingModal(selectedRoom?.resourceId)}
          >
            <Plus size={16} />
            {tr('Book resource')}
          </button>
        </div>
      </header>

      <div className="rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-slate-500">
              {tr('Snapshot')}: {snapshotDate.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}{' '}
              • {snapshotDate.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
            </div>
            <div className="mt-1 text-xs text-slate-400">
              {tr('Rooms are green when available and red when occupied at the selected time.')}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">{tr('Available')}</span>
            <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700">{tr('Taken')}</span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{tr('Unavailable')}</span>
          </div>
        </div>
      </div>

      {notice ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>
      ) : null}
      {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Selected floors')}</p>
          <p className="mt-1 text-2xl font-semibold text-ink">{selectedFloorPlans.length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Available now')}</p>
          <p className="mt-1 text-2xl font-semibold text-ink">{availableRoomCount}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Taken now')}</p>
          <p className="mt-1 text-2xl font-semibold text-ink">{occupiedRoomCount}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Unavailable')}</p>
          <p className="mt-1 text-2xl font-semibold text-ink">{maintenanceRoomCount}</p>
        </div>
      </section>

      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {tr('Loading room maps...')}
          </span>
        </div>
      ) : floorPlans.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
          <p>{tr('No active room floor plans are configured yet.')}</p>
          <button
            type="button"
            onClick={() => navigate('/settings/room-management')}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white"
          >
            <MapIcon size={15} />
            {tr('Set up room management')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <section className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-ink">{tr('Floors')}</h2>
                  <p className="text-sm text-slate-500">{tr('Select one or many floors to build the live room board for the current day.')}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {floorPlans.map((plan) => {
                    const selected = selectedFloorIds.includes(plan.id);
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => toggleFloor(plan.id)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                          selected ? 'bg-ink text-white' : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        {plan.floorLabel}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {selectedFloorPlans.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
                {tr('Choose at least one floor to display the live room map.')}
              </div>
            ) : (
              roomSnapshotRows.map((plan) => (
                <section key={plan.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 px-6 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h2 className="text-lg font-semibold text-ink">{plan.floorLabel}</h2>
                        <p className="text-sm text-slate-500">
                          {plan.name} • {plan.rooms.length} {plan.rooms.length === 1 ? tr('room mapped') : tr('rooms mapped')}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-300"
                        onClick={() => navigate('/settings/room-management')}
                      >
                        {tr('Edit floor')}
                      </button>
                    </div>
                  </div>

                  <div className="p-5">
                    <div
                      className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-slate-100"
                      style={{ aspectRatio: plan.imageWidth && plan.imageHeight ? `${plan.imageWidth}/${plan.imageHeight}` : '16 / 10' }}
                    >
                      <img src={plan.imageUrl} alt={plan.floorLabel} className="h-full w-full object-contain" />
                      <div className="absolute inset-0">
                        {plan.rooms.map((room) => {
                          const selected = room.id === selectedRoomId;
                          return (
                            <button
                              key={room.id}
                              type="button"
                              onClick={() => setSelectedRoomId(room.id)}
                              className={`absolute overflow-hidden rounded-2xl border-2 p-2 text-left transition ${statusTone(room.status)} ${
                                selected ? 'ring-2 ring-ink/70 ring-offset-2' : ''
                              }`}
                              style={{
                                left: `${room.x * 100}%`,
                                top: `${room.y * 100}%`,
                                width: `${room.width * 100}%`,
                                height: `${room.height * 100}%`,
                              }}
                            >
                              <div className="flex h-full flex-col justify-between">
                                <div className="truncate text-sm font-semibold">{room.label || room.resource?.name || tr('Room')}</div>
                                <div className="mt-2 flex items-center justify-between gap-2">
                                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusPillTone(room.status)}`}>
                                    {room.status === 'occupied' ? tr('Taken') : room.status === 'maintenance' ? tr('Unavailable') : tr('Open')}
                                  </span>
                                  <span className="truncate text-[11px] opacity-80">{room.resource?.type || ''}</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </section>
              ))
            )}
          </section>

          <aside className="space-y-4">
            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-5 py-4">
                <h2 className="text-base font-semibold text-ink">{tr('Selected room')}</h2>
                <p className="text-xs text-slate-500">{tr('Current occupancy snapshot plus the full-day booking list for the room.')}</p>
              </div>
              <div className="space-y-4 px-5 py-4">
                {selectedRoom ? (
                  <>
                    <div>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-lg font-semibold text-ink">{selectedRoom.label || selectedRoom.resource?.name || tr('Room')}</div>
                          <div className="text-xs text-slate-500">
                            {selectedRoom.resource?.type}
                            {selectedRoom.resource?.locationId ? ` • ${selectedRoom.resource.locationId}` : ''}
                          </div>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusPillTone(selectedRoom.status)}`}>
                          {selectedRoom.status === 'occupied' ? tr('Taken') : selectedRoom.status === 'maintenance' ? tr('Unavailable') : tr('Available')}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white"
                      onClick={() => openBookingModal(selectedRoom.resourceId)}
                    >
                      <Plus size={15} />
                      {tr('Book this room')}
                    </button>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                      <div className="text-xs uppercase tracking-[0.28em] text-slate-400">{tr('Now')}</div>
                      {selectedRoom.currentReservation ? (
                        <div className="mt-3 space-y-2">
                          <div className="text-sm font-semibold text-ink">{selectedRoom.currentReservation.title}</div>
                          <div className="text-xs text-slate-500">
                            {formatRange(selectedRoom.currentReservation.start, selectedRoom.currentReservation.end, locale)}
                          </div>
                          {selectedRoom.currentAppointment ? (
                            <div className="text-xs text-slate-600">
                              {selectedRoom.currentAppointment.patient.name} • {selectedRoom.currentAppointment.provider.name}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-emerald-700">{tr('No active reservation at the selected time.')}</div>
                      )}
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                      <div className="text-xs uppercase tracking-[0.28em] text-slate-400">{tr('Next')}</div>
                      {selectedRoom.nextReservation ? (
                        <div className="mt-3 space-y-2">
                          <div className="text-sm font-semibold text-ink">{selectedRoom.nextReservation.title}</div>
                          <div className="text-xs text-slate-500">
                            {formatRange(selectedRoom.nextReservation.start, selectedRoom.nextReservation.end, locale)}
                          </div>
                          {selectedRoom.nextAppointment ? (
                            <div className="text-xs text-slate-600">
                              {selectedRoom.nextAppointment.patient.name} • {selectedRoom.nextAppointment.provider.name}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-slate-500">{tr('No more bookings scheduled for this room today.')}</div>
                      )}
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-[0.28em] text-slate-400">{tr('Day schedule')}</div>
                      <div className="mt-3 space-y-2">
                        {selectedRoomReservations.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                            {tr('No reservations on this day for the selected room.')}
                          </div>
                        ) : (
                          selectedRoomReservations.map((reservation) => {
                            const appointment = appointmentByReservationId.get(reservation.id);
                            return (
                              <div key={reservation.id} className="rounded-2xl border border-slate-200 px-3 py-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <div className="text-sm font-semibold text-ink">{reservation.title}</div>
                                    <div className="text-xs text-slate-500">{formatRange(reservation.start, reservation.end, locale)}</div>
                                    {appointment ? (
                                      <div className="mt-1 text-xs text-slate-600">
                                        {appointment.patient.name} • {appointment.provider.name}
                                      </div>
                                    ) : null}
                                  </div>
                                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${reservationTone(reservation.status)}`}>
                                    {reservation.status}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-500">{tr('Click a room on the map to inspect its occupancy and day schedule.')}</div>
                )}
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-5 py-4">
                <h2 className="text-base font-semibold text-ink">{tr('Visible reservation feed')}</h2>
                <p className="text-xs text-slate-500">{tr('Bookings for the rooms shown on the selected floors.')}</p>
              </div>
              <div className="max-h-[520px] space-y-2 overflow-y-auto px-5 py-4">
                {visibleReservations.length === 0 ? (
                  <p className="text-sm text-slate-500">{tr('No reservations on the selected floors for this day.')}</p>
                ) : (
                  visibleReservations.map((reservation) => {
                    const appointment = appointmentByReservationId.get(reservation.id);
                    const apptManaged = isAppointmentReservation(reservation, appointmentByReservationId);
                    return (
                      <div key={reservation.id} className="rounded-2xl border border-slate-200 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-ink">{reservation.title}</p>
                            <p className="text-xs text-slate-500">
                              {(reservation.resource?.name || tr('Resource'))} • {formatRange(reservation.start, reservation.end, locale)}
                            </p>
                            {appointment ? (
                              <p className="mt-1 text-xs text-slate-600">
                                {appointment.patient.name} • {appointment.provider.name}
                              </p>
                            ) : null}
                          </div>
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${reservationTone(reservation.status)}`}>
                            {reservation.status}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center justify-end">
                          {apptManaged ? (
                            <span className="text-[11px] text-slate-500">{tr('Managed by appointment')}</span>
                          ) : (
                            <button
                              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-50"
                              onClick={() => deleteReservation(reservation)}
                            >
                              <AlertTriangle size={12} />
                              {tr('Release')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </aside>
        </div>
      )}

      {showBookingModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-ink">{tr('Book resource')}</h3>
            <p className="mt-1 text-xs text-slate-500">{tr('Create a manual booking for any shared room or schedulable asset.')}</p>

            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                <span className="mb-1 block text-xs font-semibold text-slate-600">{tr('Resource')}</span>
                <select
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10"
                  value={bookingForm.resourceId}
                  onChange={(event) => setBookingForm((prev) => ({ ...prev, resourceId: event.target.value }))}
                >
                  <option value="">{tr('Select resource')}</option>
                  {resources.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name} ({resource.type}){resource.active ? '' : ` • ${tr('Inactive')}`}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm">
                <span className="mb-1 block text-xs font-semibold text-slate-600">{tr('Title')}</span>
                <input
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10"
                  value={bookingForm.title}
                  onChange={(event) => setBookingForm((prev) => ({ ...prev, title: event.target.value }))}
                  placeholder={tr('Reason or usage')}
                />
              </label>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block text-xs font-semibold text-slate-600">{tr('Start')}</span>
                  <input
                    type="datetime-local"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10"
                    value={bookingForm.start}
                    onChange={(event) => setBookingForm((prev) => ({ ...prev, start: event.target.value }))}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-xs font-semibold text-slate-600">{tr('End')}</span>
                  <input
                    type="datetime-local"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10"
                    value={bookingForm.end}
                    onChange={(event) => setBookingForm((prev) => ({ ...prev, end: event.target.value }))}
                  />
                </label>
              </div>

              <label className="block text-sm">
                <span className="mb-1 block text-xs font-semibold text-slate-600">{tr('Status')}</span>
                <select
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10"
                  value={bookingForm.status}
                  onChange={(event) => setBookingForm((prev) => ({ ...prev, status: event.target.value }))}
                >
                  <option value="booked">{tr('booked')}</option>
                  <option value="blocked">{tr('blocked')}</option>
                  <option value="maintenance">{tr('maintenance')}</option>
                </select>
              </label>
            </div>

            {bookingError ? <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{bookingError}</p> : null}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300"
                onClick={() => setShowBookingModal(false)}
                disabled={bookingBusy}
              >
                {tr('Cancel')}
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                onClick={submitBooking}
                disabled={bookingBusy}
              >
                {bookingBusy ? <Loader2 size={14} className="animate-spin" /> : <Clock3 size={14} />}
                {tr('Save booking')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
